<script setup lang="ts">
import { computed, toRef } from "vue";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import type { ChartCard } from "@/types";
import { Select } from "@/components/episto-ui/select";
import { Input } from "@/components/ui/input";
import PopulationField from "./CardOptionFields/PopulationField.vue";
import { useAnalysisContext, useCardPopulation } from "./analysisData";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";
import {
  DEFAULT_WORD_CLOUD_CONTRAST,
  DEFAULT_WORD_CLOUD_LAYOUT,
  DEFAULT_WORD_CLOUD_PALETTE,
  WORD_CLOUD_CONTRASTS,
  WORD_CLOUD_COUNT_BY,
  WORD_CLOUD_LAYOUTS,
  WORD_CLOUD_PALETTES,
  wordCloudColors,
  wordCloudCountBy,
  WORD_CLOUD_MAX_WORDS,
} from "./wordCloudPalettes";

const props = defineProps({
  card: { type: Object, required: true },
});

const card = toRef(props, "card");
const cardsStore = useResultsV2CardsStore();
const { availableCards } = useAnalysisContext();
const { choices: populationChoices, selected: selectedPopulation } = useCardPopulation(card);

const OPEN_TYPES = ["q_open", "q_open_multiple"];
const { min: MIN_MAX_WORDS, max: MAX_MAX_WORDS, default: DEFAULT_MAX_WORDS } = WORD_CLOUD_MAX_WORDS;

const questionOptions = computed(() =>
  (availableCards.value as ChartCard[])
    .filter((c) => OPEN_TYPES.includes(c.type || ""))
    .map((c) => ({ value: c.questions?.[0]?.id, label: c.name || c.token }))
    .filter((option) => option.value != null),
);

function setContent(patch: Record<string, unknown>) {
  cardsStore.updateCardLocal(props.card.id, { content: { ...props.card.content, ...patch } });
}

const question = computed({
  get: () => props.card.content?.question_id || null,
  set: (value: number | null) => setContent({ question_id: value }),
});

const questionType = computed(
  () => (availableCards.value as ChartCard[]).find((c) => c.questions?.[0]?.id === question.value)?.type,
);

const countBy = computed({
  get: () => wordCloudCountBy(props.card.content?.count_by, questionType.value),
  set: (value: string) => setContent({ count_by: value }),
});

// Clamped on blur: clamping on every keystroke would turn "4" into 10 before the "0" arrives.
const maxWords = computed({
  get: () => {
    const value = props.card.content?.max_words;
    return value === undefined ? DEFAULT_MAX_WORDS : (value ?? "");
  },
  set: (value: string | number) => setContent({ max_words: parseInt(String(value), 10) || null }),
});

function clampMaxWords() {
  const value = props.card.content?.max_words;
  const clamped = Math.min(MAX_MAX_WORDS, Math.max(MIN_MAX_WORDS, value || DEFAULT_MAX_WORDS));
  if (clamped !== value) setContent({ max_words: clamped });
}

const excludedWords = computed({
  get: () => props.card.content?.excluded_words || "",
  set: (value: string) => setContent({ excluded_words: value }),
});

const paletteOptions = WORD_CLOUD_PALETTES.map((palette) => ({ value: palette.id, label: palette.label }));

const palette = computed({
  get: () => props.card.content?.palette || DEFAULT_WORD_CLOUD_PALETTE,
  set: (value: string) =>
    setContent(
      value === "custom" && !props.card.content?.colors?.length
        ? { palette: value, colors: wordCloudColors(palette.value, undefined) }
        : { palette: value },
    ),
});

const customColors = computed<string[]>(() => wordCloudColors("custom", props.card.content?.colors));

function setCustomColor(idx: number, value: string) {
  const colors = [...customColors.value];
  colors[idx] = value;
  setContent({ colors });
}

const contrast = computed({
  get: () => props.card.content?.size_contrast || DEFAULT_WORD_CLOUD_CONTRAST,
  set: (value: string) => setContent({ size_contrast: value }),
});

const compareOptions = computed(() => [
  { value: "none", label: "None" },
  ...populationChoices.value.filter((choice) => choice.token !== selectedPopulation.value).map((choice) => ({ value: choice.token, label: choice.label })),
]);

const comparePopulation = computed({
  get: () => compareOptions.value.find((option) => option.value === props.card.content?.compare_population)?.value || "none",
  set: (value: string) => setContent({ compare_population: value === "none" ? null : value }),
});

const layout = computed({
  get: () => props.card.content?.layout || DEFAULT_WORD_CLOUD_LAYOUT,
  set: (value: string) => setContent({ layout: value }),
});

const displayCount = computed(
  () =>
    [
      comparePopulation.value === "none" && palette.value !== DEFAULT_WORD_CLOUD_PALETTE,
      contrast.value !== DEFAULT_WORD_CLOUD_CONTRAST,
      layout.value !== DEFAULT_WORD_CLOUD_LAYOUT,
    ].filter(Boolean).length,
);
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <OptionRow label="Open question">
      <Select
        :model-value="question"
        :options="questionOptions"
        placeholder="Select an open question..."
        filterable
        @update:model-value="question = $event" />
    </OptionRow>

    <OptionRow
      label="Count"
      hint="Whole answers keep names together (Louis Vuitton, New Balance); words split long answers.">
      <Select
        :model-value="countBy"
        :options="WORD_CLOUD_COUNT_BY"
        @update:model-value="countBy = String($event)" />
    </OptionRow>

    <OptionRow label="Max words">
      <Input
        type="number"
        :min="MIN_MAX_WORDS"
        :max="MAX_MAX_WORDS"
        :model-value="maxWords"
        @update:model-value="maxWords = $event"
        @blur="clampMaxWords" />
    </OptionRow>

    <OptionRow
      label="Excluded words"
      hint="Comma-separated. The common connectors of the survey's languages are already left out; add the brand or product names that would otherwise dominate.">
      <Input
        :model-value="excludedWords"
        placeholder="e.g. brand, product"
        @update:model-value="excludedWords = $event" />
    </OptionRow>

    <PopulationField :card="card" />

    <OptionRow
      label="Compare with"
      hint="Splits the card in two clouds: the words that set each population apart, not the ones they share.">
      <Select
        :model-value="comparePopulation"
        :options="compareOptions"
        @update:model-value="comparePopulation = String($event)" />
    </OptionRow>
  </OptionSection>

  <OptionSection
    id="display"
    title="Display"
    :count="displayCount">
    <OptionRow
      v-if="comparePopulation === 'none'"
      label="Colors">
      <Select
        :model-value="palette"
        :options="paletteOptions"
        @update:model-value="palette = $event" />
      <div
        v-if="palette === 'custom'"
        class="mt-2 flex items-center gap-1.5">
        <label
          v-for="(color, idx) in customColors"
          :key="idx"
          class="relative block h-7 w-7 shrink-0 cursor-pointer overflow-hidden rounded border border-border"
          :style="{ backgroundColor: color }"
          :aria-label="`Color ${idx + 1}`">
          <input
            type="color"
            :value="color"
            class="sr-only"
            @input="setCustomColor(idx, ($event.target as HTMLInputElement).value)" />
        </label>
      </div>
    </OptionRow>

    <OptionRow
      label="Size contrast"
      hint="How much bigger the most frequent words are than the rarest ones shown.">
      <Select
        :model-value="contrast"
        :options="WORD_CLOUD_CONTRASTS"
        @update:model-value="contrast = $event" />
    </OptionRow>

    <OptionRow label="Layout">
      <Select
        :model-value="layout"
        :options="WORD_CLOUD_LAYOUTS"
        @update:model-value="layout = $event" />
    </OptionRow>
  </OptionSection>
</template>
